<script setup>
import { computed, nextTick, onMounted, onUnmounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import Modal from 'dashboard/components/Modal.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  show: { type: Boolean, default: false },
  action: { type: String, default: 'offline' },
});

const emit = defineEmits(['update:show', 'expire', 'continue']);
const { t } = useI18n();
const remainingSeconds = ref(60);
const continueButton = ref(null);
let timer = null;
let expirationTime = 0;

const actionDescription = computed(() =>
  props.action === 'logout'
    ? t('SIDEBAR.KEEP_ALIVE_MODAL.ACTION_LOGOUT')
    : t('SIDEBAR.KEEP_ALIVE_MODAL.ACTION_OFFLINE')
);

const countdownAnnouncement = computed(() => {
  if (remainingSeconds.value > 30)
    return t('SIDEBAR.KEEP_ALIVE_MODAL.LESS_THAN_MINUTE');
  if (remainingSeconds.value > 10) {
    return t('SIDEBAR.KEEP_ALIVE_MODAL.SECONDS_REMAINING', {
      count: Math.ceil(remainingSeconds.value / 10) * 10,
    });
  }
  return t('SIDEBAR.KEEP_ALIVE_MODAL.SECONDS_REMAINING', {
    count: remainingSeconds.value,
  });
});

const stopTimer = () => {
  if (timer) clearInterval(timer);
  timer = null;
};

onMounted(async () => {
  expirationTime = Date.now() + 60000;
  timer = setInterval(() => {
    const remaining = Math.max(
      0,
      Math.ceil((expirationTime - Date.now()) / 1000)
    );
    remainingSeconds.value = remaining;
    if (remaining === 0) {
      stopTimer();
      emit('expire');
    }
  }, 250);

  await nextTick();
  continueButton.value?.$el?.focus?.();
});

onUnmounted(stopTimer);

const continueSession = () => {
  stopTimer();
  emit('continue');
  emit('update:show', false);
};
</script>

<template>
  <Modal :show="show" :on-close="continueSession">
    <section
      class="h-auto overflow-auto flex flex-col items-center justify-center p-8 text-center"
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="keepalive-title"
      aria-describedby="keepalive-description keepalive-countdown"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        class="h-16 w-16 text-amber-500 mx-auto mb-6"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        stroke-width="2"
        aria-hidden="true"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
        />
      </svg>
      <h2 id="keepalive-title" class="text-2xl font-bold text-n-slate-12 mb-2">
        {{ t('SIDEBAR.KEEP_ALIVE_MODAL.TITLE') }}
      </h2>
      <p id="keepalive-description" class="text-n-slate-11 mb-6">
        {{
          t('SIDEBAR.KEEP_ALIVE_MODAL.DESCRIPTION', {
            action: actionDescription,
          })
        }}
      </p>
      <div class="text-6xl font-black text-amber-500 mb-8" aria-hidden="true">
        {{
          t('SIDEBAR.KEEP_ALIVE_MODAL.SECONDS_SHORT', {
            count: remainingSeconds,
          })
        }}
      </div>
      <p id="keepalive-countdown" class="sr-only" aria-live="polite">
        {{ countdownAnnouncement }}
      </p>
      <NextButton
        ref="continueButton"
        type="button"
        :label="t('SIDEBAR.KEEP_ALIVE_MODAL.CONTINUE')"
        class="w-full max-w-sm h-12 text-lg shadow-lg"
        @click="continueSession"
      />
    </section>
  </Modal>
</template>
